import { Box, Card, CardActionArea, CardContent, Typography } from '@mui/material';
import { GridCheckIcon } from '@mui/x-data-grid';
import { vehicleFormHooks } from 'app-components/flux/vehicle/vehicleForm';
import { type VehicleCondition } from 'app-components/flux/vehicle/vehicleTypes';
import { tracker } from 'app-components/utils/core';
import clsx from 'clsx';
import InfoTooltipIcon from 'components/InfoTooltipIcon';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { VEHICLE_CONDITION_DEFINITIONS, VEHICLE_CONDITION_OPTIONS } from '../utils';

const useStyles = makeStyles()((theme) => ({
  mainTitle: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  tooltip: {
    color: theme.palette.icon.light,
    marginLeft: theme.spacing(0.75),
    '& .MuiSvgIcon-root': {
      fontSize: 18,
    },
  },
  conditionCardWrapper: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  conditionCard: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: 160,

    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    transition: 'border-color .15s ease, background-color .15s ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      background: theme.palette.primary.light,
    },
  },
  selectedCard: {
    borderColor: theme.palette.primary.main,
    background: theme.palette.primary.light,
    '&:hover': {
      borderColor: theme.palette.primary.main,
      background: theme.palette.primary.light,
    },
  },
  cardActionArea: {
    display: 'flex',
    alignItems: 'flex-start',
    flex: 1,
  },
  cardContent: {
    padding: theme.spacing(1, 1.5),
  },
  cardTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  checkIcon: {
    color: theme.palette.background.paper,
    backgroundColor: theme.palette.active.main,
    borderRadius: '50%',
    padding: 1,
    height: 18,
    width: 18,
  },
}));

export const ConditionStep = () => {
  const { classes } = useStyles();

  const { value: vehicleCondition, onValueChange: setVehicleCondition } = vehicleFormHooks.useField('condition');

  const getTrackerCondition = (state: VehicleCondition) => {
    switch (state) {
      case 'FAIR':
        return tracker.track(tracker.events.kbbCondition, { userAction: 'Fair' });
      case 'GOOD':
        return tracker.track(tracker.events.kbbCondition, { userAction: 'Good' });
      case 'VERY_GOOD':
        return tracker.track(tracker.events.kbbCondition, { userAction: 'Very Good' });
      case 'EXCELLENT':
        return tracker.track(tracker.events.kbbCondition, { userAction: 'Excellent' });
    }
  };

  return (
    <Box>
      <Box className={classes.mainTitle}>
        <Typography variant='headlineRegular'>What is your car&apos;s condition?</Typography>
        <InfoTooltipIcon
          className={classes.tooltip}
          tooltipPlacement='right'
          tooltipHTML={
            <Typography variant='captionRegular'>
              {vehicleCondition
                ? VEHICLE_CONDITION_DEFINITIONS[vehicleCondition]
                : 'Select a condition to display more details.'}
            </Typography>
          }
        />
      </Box>
      <Box className={classes.conditionCardWrapper}>
        {VEHICLE_CONDITION_OPTIONS.map(({ value, label, description }) => {
          return (
            <Card
              key={value}
              elevation={0}
              className={clsx(classes.conditionCard, {
                [classes.selectedCard]: vehicleCondition === value,
              })}
            >
              <CardActionArea
                disableRipple
                className={classes.cardActionArea}
                onClick={() => {
                  setVehicleCondition(value);
                  getTrackerCondition(value);
                }}
              >
                <CardContent className={classes.cardContent}>
                  <Box className={classes.cardTitle}>
                    <Typography variant='bodyMedium'>{label}</Typography>
                    {vehicleCondition === value && <GridCheckIcon className={classes.checkIcon} />}
                  </Box>
                  <Typography variant='paragraphRegular' lineHeight='normal'>
                    {description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};
